// Portal side of portal ⇄ extension SSO (see docs/ARCHITECTURE.md "SSO").
//
// The extension injects a content script on the portal origin. We talk to it
// with window.postMessage on our own origin only:
//   portal → ext   { source: "magicallylinked-portal", type: "sso-ping" | "sso-token" | "sso-logout", token? }
//   ext → portal   { source: "magicallylinked-extension", type: "sso-pong" | "sso-ack", version, signedIn }
//
// Rules:
// - Ping on load (and again when someone signs in). Any reply means the
//   extension is installed; its `signedIn` flag drives the status pill.
// - POST /api/auth/ext-token mints a NEW 90-day session every call, so we only
//   call it when (a) the extension replies signedIn:false while a user is signed
//   in here — at most once per tab session per user, plus a couple of throttled
//   retries — or (b) the user clicks Connect on /connect-extension.
// - Never hand over a token while an admin/reseller is impersonating someone:
//   that would sign the extension in as the customer and could run their
//   automations against whatever LinkedIn account is open in this browser.
// - Logging out posts sso-logout.

import { useSyncExternalStore } from "react";
import type { Me, SsoBridgeMessage, SsoBridgeReply } from "@ml/shared";
import { authApi } from "./api";

export type ExtensionStatus = "checking" | "connected" | "signed_out" | "not_detected";

export interface ExtensionState {
  status: ExtensionStatus;
  /** Extension version from its last reply. */
  version: string | null;
  lastReplyAt: number | null;
  /** True while we're fetching/sending a token. */
  syncing: boolean;
}

const SYNC_KEY = "gf.sso.syncedFor";
const DETECT_AFTER_MS = 3000;
/** Automatic (non-click) token handoffs per page load. */
const MAX_AUTO_SYNCS = 2;
const AUTO_SYNC_GAP_MS = 20_000;

let state: ExtensionState = { status: "checking", version: null, lastReplyAt: null, syncing: false };
const listeners = new Set<() => void>();
let installed = false;
let detectTimer: number | undefined;
let currentUser: { id: string; impersonating: boolean } | null = null;
let inflight: Promise<boolean> | null = null;
let autoSyncs = 0;
let lastAutoAt = 0;

function setState(patch: Partial<ExtensionState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

function post(msg: SsoBridgeMessage) {
  window.postMessage(msg, window.location.origin);
}

function readSynced(): string | null {
  try {
    return sessionStorage.getItem(SYNC_KEY);
  } catch {
    return null;
  }
}
function writeSynced(v: string | null) {
  try {
    if (v) sessionStorage.setItem(SYNC_KEY, v);
    else sessionStorage.removeItem(SYNC_KEY);
  } catch {
    /* storage blocked — the per-load cap still applies */
  }
}

/** The extension is installed but signed out: maybe give it a token (throttled, capped). */
function maybeAutoSync() {
  const user = currentUser;
  if (!user || user.impersonating) return;
  const now = Date.now();
  if (autoSyncs >= MAX_AUTO_SYNCS || now - lastAutoAt < AUTO_SYNC_GAP_MS) return;
  // Already handed one over this tab session and it's still signed out → the
  // first retry is allowed (it may have restarted); after that, wait for a click.
  if (readSynced() === user.id && autoSyncs >= 1) return;
  autoSyncs++;
  lastAutoAt = now;
  void syncExtension();
}

function onMessage(e: MessageEvent) {
  if (e.source !== window || e.origin !== window.location.origin) return;
  const d = e.data as Partial<SsoBridgeReply> | null;
  if (!d || typeof d !== "object" || d.source !== "magicallylinked-extension") return;
  if (d.type !== "sso-ack" && d.type !== "sso-pong") return;
  window.clearTimeout(detectTimer);
  const signedIn = d.signedIn === true;
  setState({
    status: signedIn ? "connected" : "signed_out",
    version: typeof d.version === "string" ? d.version : state.version,
    lastReplyAt: Date.now(),
  });
  if (!signedIn) maybeAutoSync();
}

/** Install the message listener and ping the extension. Safe to call repeatedly. */
export function initSsoBridge() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("message", onMessage);
  window.addEventListener("focus", pingExtension);
  window.setInterval(() => { if (document.visibilityState === "visible") pingExtension(); }, 30_000);
  pingExtension();
}

/**
 * Ask the extension to announce itself. Pings a few times because the content
 * script may attach after our first message; settles on "not_detected" after ~3s.
 */
export function pingExtension() {
  if (typeof window === "undefined") return;
  const startedAt = Date.now();
  if (state.status === "not_detected") setState({ status: "checking" });
  const ping = () => post({ source: "magicallylinked-portal", type: "sso-ping" });
  ping();
  window.setTimeout(ping, 700);
  window.setTimeout(ping, 1800);
  window.clearTimeout(detectTimer);
  detectTimer = window.setTimeout(() => {
    if (state.lastReplyAt === null || state.lastReplyAt < startedAt) setState({ status: "not_detected", version: null });
  }, DETECT_AFTER_MS);
}

/**
 * Tell the bridge who's signed in (called by AuthProvider whenever `me`
 * changes). A new identity re-pings so a signed-out extension can ask for a token.
 */
export function setSsoUser(user: Me | null) {
  if (!user || user.mustChangePassword) {
    currentUser = null;
    return;
  }
  const impersonating = !!user.impersonatorId;
  const changed = currentUser?.id !== user.id || currentUser?.impersonating !== impersonating;
  currentUser = { id: user.id, impersonating };
  if (!changed || impersonating) return;
  autoSyncs = 0;
  // If we already know it's signed out, act now; otherwise its pong will tell us.
  if (state.status === "signed_out") maybeAutoSync();
  else pingExtension();
}

/**
 * Mint an extension token and post it to the extension. Returns true when a
 * token was posted (the ack arrives asynchronously via the status store).
 */
export async function syncExtension(): Promise<boolean> {
  const user = currentUser;
  if (!user || user.impersonating || typeof window === "undefined") return false;
  if (inflight) return inflight;
  setState({ syncing: true });
  inflight = (async () => {
    try {
      const res = await authApi.extToken();
      if (!res.token) return false;
      // The user might have logged out while we waited.
      if (currentUser?.id !== user.id) return false;
      post({ source: "magicallylinked-portal", type: "sso-token", token: res.token });
      writeSynced(user.id);
      return true;
    } catch {
      return false;
    } finally {
      inflight = null;
      setState({ syncing: false });
    }
  })();
  return inflight;
}

/** Sign the extension out (portal logout). */
export function ssoLogout() {
  currentUser = null;
  writeSynced(null);
  autoSyncs = 0;
  if (typeof window !== "undefined") post({ source: "magicallylinked-portal", type: "sso-logout" });
  if (state.status === "connected") setState({ status: "signed_out" });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function snapshot() {
  return state;
}

/**
 * Live extension status for UI:
 *   const ext = useExtensionStatus();
 *   ext.status === "connected" | "signed_out" | "not_detected" | "checking"
 */
export function useExtensionStatus() {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  return {
    ...s,
    connected: s.status === "connected",
    detected: s.status === "connected" || s.status === "signed_out",
    /** Re-ping (e.g. after the user installs it). */
    recheck: pingExtension,
    /** Explicit "Connect" click: hand over a fresh token now. */
    connect: syncExtension,
  };
}
